import { DEFAULT_POLICY_TEMPLATE, PolicyData, PolicyTemplate, Protocols } from '../../../../types/policies';
import { RadioGroupProps } from '@silverfort/sf-components';
import {
    ACTION_DISCLAIMER_BASE,
    APPLICATION_TEMPLATE_PROTOCOL_TOOLTIP,
    DC_IN_DESTINATION_ERROR_MESSAGE,
    POLICY_FIELDS_FORM_RELEVANCE_VALIDATORS
} from '../consts/forms';
import { SwitcherTabProps } from '@silverfort/sf-components/dist/SwitcherTabs/SwitcherTabs';
import { POLICY_ACTION_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { PolicyConfiguration } from '../../types/PolicyConfiguration';
import { FieldErrors, FieldName, PolicyFormValues } from '../types/forms';
import { PROTOCOL_TO_DETAILS_MAP } from '../consts/policy';
import { allComputersResource, unresolvedResources } from '../../common';
import { TOOLTIP_PROP_OPTIONS } from '../../../common/LockedFeatures/consts';
import { POLICY_SELECT_DESTINATION_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { ArrayElement } from '../../../../types/types';

export const getPolicyFromValuesFromTemplate = (template: PolicyTemplate): PolicyFormValues => {
    const {
        id,
        name,
        description,
        isFavorite,
        editableFields,
        typeId,
        supportedActions,
        supportedProtocols,
        authTypes,
        spns,
        risks,
        disclaimer,
        mfaNote,
        ...formValues
    } = template;

    return { ...formValues, protocol: supportedProtocols?.[0], action_type: supportedActions[0] };
};

export const getProtocolRadioGroupOptions = (template: PolicyTemplate) => {
    const { supportedProtocols } = template;

    if (!supportedProtocols) {
        return [];
    }

    const options: RadioGroupProps['options'] = supportedProtocols.map((protocol) => ({
        value: protocol,
        title: PROTOCOL_TO_DETAILS_MAP[protocol]
    }));

    return options;
};

export const getActionSwitcherTabItems = (
    supportedActions: number[],
    isAuthenticationModuleEnabled: boolean,
    isMfaUsersModuleEnabled: boolean
): SwitcherTabProps<number>['items'] => {
    const actionsOrder = [
        POLICY_ACTION_TYPES.NOTIFY.value,
        POLICY_ACTION_TYPES.MFA.value,
        POLICY_ACTION_TYPES.DENY.value
    ];

    const actionTypeSwitchItems: SwitcherTabProps<number>['items'] = Object.values(POLICY_ACTION_TYPES)
        .filter(({ value }) => supportedActions.includes(value))
        .map(({ value, title }) => {
            const switcherTabItem = {
                value,
                text: title,
                large: true
            };

            if (value === POLICY_ACTION_TYPES.DENY.value && !isAuthenticationModuleEnabled) {
                return {
                    ...switcherTabItem,
                    disabled: true,
                    tooltipProps: TOOLTIP_PROP_OPTIONS.AUTHENTICATION_FIREWALL_MODULE_REQUIRED
                };
            }

            if (
                (value === POLICY_ACTION_TYPES.MFA.value || value === POLICY_ACTION_TYPES.NOTIFY.value) &&
                !isMfaUsersModuleEnabled
            ) {
                return {
                    ...switcherTabItem,
                    disabled: true,
                    tooltipProps: TOOLTIP_PROP_OPTIONS.MFA_MODULE_REQUIRED
                };
            }

            return switcherTabItem;
        })
        .sort((firstAction, secondAction) => {
            return actionsOrder.indexOf(firstAction.value) - actionsOrder.indexOf(secondAction.value);
        });

    return actionTypeSwitchItems;
};

export const getAllComputersObject = (formValues: PolicyFormValues, configuration: PolicyConfiguration): unknown => {
    return {
        ...allComputersResource,
        match_all_services: formValues.match_all_services ?? true,
        selectedServices:
            formValues.services?.map((service) => {
                return { service_identifier: service };
            }) ?? [],
        services: configuration.GlobalServices,
        name: allComputersResource.display_name
    };
};

export const getUnresolvedResourcesObject = (formValues: PolicyFormValues, configuration: PolicyConfiguration): unknown => {
    return {
        ...unresolvedResources,
        match_all_services: formValues.unresolved_resources_match_all_services ?? true,
        selectedServices: formValues.unresolved_resources_services?.map((service) => {
            return { service_identifier: service };
        }) || [],
        services: configuration.GlobalServices,
        name: unresolvedResources.display_name
    };
};

export const getProtocolFieldTooltip = ({ typeId }: PolicyTemplate): string | undefined => {
    if (typeId === DEFAULT_POLICY_TEMPLATE) {
        return APPLICATION_TEMPLATE_PROTOCOL_TOOLTIP;
    }

    return undefined;
};

export const getUsersAndGroupsTooltip = (template: PolicyTemplate): string | undefined => {
    if(template.usersAndGroupsTooltip){
        return template.usersAndGroupsTooltip;
    }
    return undefined;
};

export const getSourceTooltip = (template: PolicyTemplate): string | undefined => {
    if(template.sourceTooltip){
        return template.sourceTooltip;
    }
    return undefined;
};

export const getDestinationTooltip = (template: PolicyTemplate): string | undefined => {
    if(template.destinationTooltip){
        return template.destinationTooltip;
    }
    return undefined;
};

export const getServersSelectedLabels = (servers: PolicyFormValues['servers']): string[] | undefined => {
    const areAllServersExcluded = servers?.every(
        (server) => server.excluded || server.guid === allComputersResource.guid
    );
    if (servers?.length === 0 || areAllServersExcluded) {
        return [allComputersResource.display_name];
    }

    return servers?.map((server) => server.name);
};

export const getServersSelectedEntities = (servers: PolicyFormValues['servers'], allComputersObject: unknown, unresolvedResourcesObject: unknown, destination_types: PolicyFormValues['destination_types']) => {
    const doAnyIncludedServerExist = servers?.some((server) => !server.excluded);
    const selectedServers: PolicyFormValues['servers'] = [];
    if (!doAnyIncludedServerExist) {
        selectedServers.push(allComputersObject);
    }
    if (destination_types?.includes(POLICY_SELECT_DESTINATION_OPTIONS.UNRESOLVED)) {
        selectedServers.push(unresolvedResourcesObject);
    }
    if (servers?.length) {
        selectedServers.push(...servers);
    }

    return selectedServers;
};

export const isFieldEditable = (
    fieldName: keyof PolicyFormValues,
    template: PolicyTemplate,
    formValues: PolicyFormValues
) => {
    return (
        template.editableFields.includes(fieldName) &&
        POLICY_FIELDS_FORM_RELEVANCE_VALIDATORS[fieldName]?.(formValues) !== false
    );
};

export const getMissingFields = (template: PolicyTemplate, policyFormValues: PolicyFormValues) => {
    const missingFields: (keyof PolicyFormValues)[] = [];
    const fieldsToValidate: (keyof PolicyFormValues)[] = [
        'protocol',
        'entities',
        'servers',
        'servers_ips',
        'action_type',
        'display_name',
        'allowed_token_types'
    ];

    if (!policyFormValues.policyName) {
        missingFields.push('policyName');
    }

    fieldsToValidate.forEach((fieldName) => {
        if (isFieldEditable(fieldName, template, policyFormValues) && !policyFormValues[fieldName]) {
            missingFields.push(fieldName);
        }
    });

    if (isFieldEditable('entities', template, policyFormValues) && !policyFormValues.entities?.length) {
        missingFields.push('entities');
    }

    if (isFieldEditable('servers', template, policyFormValues) && !policyFormValues.servers) {
        missingFields.push('servers');
    }

    if (isFieldEditable('servers_ips', template, policyFormValues) && !policyFormValues.servers_ips?.length) {
        missingFields.push('servers_ips');
    }

    return missingFields;
};

const validateDcInDestination = (
    policyFormValues: PolicyFormValues,
    policyConfiguration: PolicyConfiguration
): boolean => {
    const { servers, protocol } = policyFormValues;

    return (
        !!servers &&
        protocol === Protocols.KERBEROS_NTLM &&
        servers.some(
            (server) => !server.excluded && policyConfiguration.DomainControllers.some((dc) => dc.guid === server.guid)
        )
    );
};

const validateDcInApplicationIp = (
    policyFormValues: PolicyFormValues,
    policyConfiguration: PolicyConfiguration
): boolean => {
    const { servers_ips, protocol } = policyFormValues;

    return (
        !!servers_ips &&
        protocol === Protocols.LDAP &&
        servers_ips.some((server_ip) =>
            policyConfiguration.DomainControllers.some((dc) => dc.ipAddress === server_ip.address)
        )
    );
};

type Validators = { [key in FieldName]?: { validate: () => boolean; message: string }[] };

export const getFormFieldsErrors = (
    policyFormValues: PolicyFormValues,
    policyConfiguration: PolicyConfiguration
): FieldErrors => {
    const fieldErrors: FieldErrors = {};

    const validators: Validators = {
        servers: [
            {
                validate: () => validateDcInDestination(policyFormValues, policyConfiguration),
                message: DC_IN_DESTINATION_ERROR_MESSAGE
            }
        ],
        servers_ips: [
            {
                validate: () => validateDcInApplicationIp(policyFormValues, policyConfiguration),
                message: DC_IN_DESTINATION_ERROR_MESSAGE
            }
        ]
    };

    Object.entries(validators).forEach(([fieldName, fieldValidators]) => {
        fieldValidators.forEach(({ validate, message }) => {
            if (validate()) {
                if (!fieldErrors[fieldName]) {
                    fieldErrors[fieldName] = [message];
                }

                fieldErrors[fieldName] = [...fieldErrors[fieldName], message];
            }
        });
    });

    return fieldErrors;
};

export const convertFormValuesToPolicy = (formValues: PolicyFormValues): Partial<PolicyData> => {
    const { policyName, protocol, ...policyValues } = formValues;
    return {
        ...policyValues,
        name: policyName,
        kerberos_enabled: protocol === Protocols.KERBEROS_NTLM || protocol === Protocols.KERBEROS,
        ntlm_enabled: protocol === Protocols.KERBEROS_NTLM || protocol === Protocols.NTLM,
        ldap_enabled: protocol === Protocols.LDAP
    };
};

export const getSelectedServicesForServer = (server, spns?: PolicyTemplate['spns']) => {
    if (!spns) {
        return [];
    }

    return server.services
        .filter((service) => spns.some((spn) => service?.match(`^${spn}`)))
        .map((service) => ({
            service_identifier: service,
            type: 1
        }));
};


export const mapSelectedServer = (server: ArrayElement<PolicyFormValues['servers']>, spns?: PolicyTemplate['spns']) => {
    return {
        type: server.type,
        guid: server.guid,
        name: server.name,
        path: server.path,
        excluded: server.excluded,
        domain_name: server.domain_name,
        selectedServices: getSelectedServicesForServer(server, spns),
        services: server.services,
        is_deleted: server.is_deleted,
        user_with_spn: server.user_with_spn,
        match_all_services: server.match_all_services
    }
}

export const getActionDisclaimer = (template: PolicyTemplate) => {
    if (template.supportedActions.length > 1 && template.supportedActions.some((action) => action === POLICY_ACTION_TYPES.NOTIFY.value)) {
        const nonNotifyActions = template.supportedActions
            .filter((action) => action !== POLICY_ACTION_TYPES.NOTIFY.value)
            .sort((a, b) => b - a)
            .map((action) => Object.values(POLICY_ACTION_TYPES).find(({ value }) => value === action)?.title);

        return `${ACTION_DISCLAIMER_BASE} ${nonNotifyActions.join(' or ')}.`;
    }

    return '';
};
